import { SearchIcon } from "@chakra-ui/icons"
import { Box, Flex, Image, Input, InputGroup, InputRightElement, Skeleton, Text, useBreakpointValue } from "@chakra-ui/react"
import styles from "../../Common/Style/Style.module.css";
import { useEffect, useState } from "react";
import { useDebounce } from "../Hook/useDebounce.hook";
import { findCompanyBySearchTerm } from "../Actions/findCompanyBySearchTerm.action";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

export const CorporateLandingComponent = () => {
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [companies, setCompanies] = useState<any>([]);
    const [companiesLogoUrls, setCompaniesLogoUrls] = useState<string[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const navigate = useNavigate();
    const beneficiaryData = useSelector((state: any) => state.beneficiary);

    const isMobile = useBreakpointValue({ base: true, lg: false });

    // Fetch company 
    useEffect(() => {
        const fetchCompanies = async (term: string) => {
            if (!term.trim()) {
                setCompanies([]);
                setCompaniesLogoUrls([]);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const data = await findCompanyBySearchTerm(term);
                const topCompanies = data.slice(0, 4);
                setCompanies(topCompanies);

                const logoBlobUrls = await Promise.all(
                    topCompanies.map(async (company:any) => {
                        try {
                            
                            if (company?.logo.includes("apis.pococare.com")) {
                                const response = await fetch(company.logo);
                                const blob = await response.blob();
                                return URL.createObjectURL(blob);
                              } else {
                                return company.logo; // Or return null / fallback image URL if you prefer
                              }
                              
                        } catch (err) {
                            console.error(`Failed to load logo for ${company.companyName}`, err);
                            return "";
                        }
                    })
                );

                setCompaniesLogoUrls(logoBlobUrls);
            } catch (err) {
                console.error("Failed to fetch companies:", err);
                setError("Failed to fetch companies. Check Internet and please try again.");
                setCompanies([]);
                setCompaniesLogoUrls([]);
            } finally {
                setIsLoading(false);
            }
        };

        if(beneficiaryData.length){ 
            return navigate('/dashboard')
        }

        fetchCompanies(debouncedSearchTerm);
    }, [debouncedSearchTerm, beneficiaryData, navigate]);


    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
    };

    return (
        <Box width={"100%"} background={"rgba(254, 29, 143, 0.03)"}>
            <Box width={isMobile ? "90%":"70%"} margin={"auto"} paddingTop={isMobile ? "60px":"140px"} paddingBottom={isMobile ? "40px":"106px"}>
                <Box>
                    <Flex flexDir={"row"} justifyContent={"center"}>
                        <Box hidden={isMobile} className={styles.gradientBorderLeftBox} marginTop="40px"
                        ></Box>
                        <Box>
                            <Text
                                fontFamily="Poppins"
                                fontWeight={600}
                                fontSize={isMobile ? "32px":"48px"}
                                lineHeight={isMobile ? "38.4px":"70px"}
                                textAlign="center"
                                letterSpacing="0px"
                                color={"rgba(18, 195, 149, 1)"}
                            >
                                Pococare Corporate Login
                            </Text>
                            <Text
                                width={"85%"}
                                margin={"auto"}
                                fontFamily="Poppins"
                                fontWeight="400"
                                fontSize="16px"
                                lineHeight="24px"
                                letterSpacing="0%"
                                textAlign={"center"}
                                mt="32px"
                                mx={"auto"}
                            >
                               Welcome to Pococare's corporate portal for login. Search for your organization below and select it to proceed to your dedicated organization page.
                            </Text>
                            <Box width="100%" maxWidth={isMobile ? "480px":"70%"} margin={isMobile ? "40px auto":"60px auto"}>
                                <InputGroup>
                                    <Input
                                        height="69px"
                                        type="text"
                                        value={searchTerm}
                                        placeholder="Search by company name"
                                        focusBorderColor="rgba(18, 195, 149, 1)"
                                        borderRadius="10px"
                                        border="1px solid rgba(0, 0, 0, 0.28)"
                                        boxShadow="0px 8px 20.9px 0px rgba(254, 29, 143, 0.13)"
                                        pr={10}
                                        _hover={{ borderColor: 'green.300' }}
                                        bg={"#fff"}
                                        variant="outline"
                                        fontFamily="Poppins"
                                        fontSize={isMobile ? "16px":"18px"}
                                        fontWeight={400}
                                        lineHeight="24px"
                                        letterSpacing="0.5px"
                                        color={"rgba(0, 0, 0, 0.5)"}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                    />
                                    <InputRightElement
                                        height="100%"
                                        pointerEvents="none"
                                        display="flex"
                                        alignItems="center"
                                    >
                                        <SearchIcon color="rgba(18, 195, 149, 1);" width={"30px"} height={"30px"} mr={"23px"} />
                                    </InputRightElement>
                                </InputGroup>
                            </Box>
                        </Box>
                        <Box hidden={isMobile} className={styles.gradientBorderRightBox} marginTop="40px"></Box>
                    </Flex>
                    <Box width="90%" margin="auto" mt="10px">
                        {isLoading ? (
                            <Flex flexWrap="wrap" gap={6} justifyContent="center">
                            {[1, 2, 3, 4].map((_, index) => (
                              <Skeleton
                                key={index}
                                width="120px"
                                height="120px"
                                borderRadius="15px"
                                startColor="pink.100"
                                endColor="pink.300"
                              />
                            ))}
                          </Flex>
                        )
                            : error ? (<Text textAlign="center">{error}</Text>)
                                : companies.length === 0 && debouncedSearchTerm ? (
                                    <Flex justifyContent="center">
                                        <Box
                                            background="rgba(255, 255, 255, 1)"
                                            borderRadius="15px"
                                            boxShadow="0px 4px 17.1px 0px rgba(254, 29, 143, 0.12)"
                                            padding="20px"
                                            width="120px"
                                            height="120px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Text
                                                textAlign="center"
                                                fontFamily="Poppins"
                                                fontWeight="500"
                                                fontSize="14px"
                                                color="rgba(0, 0, 0, 0.7)"
                                            >
                                                Company Not Found
                                            </Text>
                                        </Box>
                                    </Flex>
                                ) : (
                                    <Flex flexWrap="wrap" gap={6} justifyContent="center">
                                        {companies.map((company: any, index: any) => (
                                            <Box
                                                key={index}
                                                background="rgba(255, 255, 255, 1)"
                                                borderRadius="15px"
                                                boxShadow="0px 4px 17.1px 0px rgba(254, 29, 143, 0.12)"
                                                padding="20px"
                                                width="120px"
                                                height="120px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                onClick={() => navigate(`/corporate/${company.webContext || company.companyName}`)}
                                                cursor="pointer"
                                                _hover={{ transform: "scale(1.05)" }}
                                                transition="transform 0.2s"
                                            >
                                                {company.logo ? (
                                                    <Image
                                                        src={companiesLogoUrls[index]}
                                                        alt={company.companyName}
                                                        maxHeight="100%"
                                                        maxWidth="100%"
                                                        objectFit="contain"
                                                    />
                                                ) : (
                                                    <Text
                                                        textAlign="center"
                                                        fontFamily="Poppins"
                                                        fontWeight="500"
                                                        fontSize="14px"
                                                        color="rgba(0, 0, 0, 0.7)"
                                                    >
                                                        {company.webContext || company.companyName}
                                                    </Text>
                                                )}
                                            </Box>
                                        ))}
                                    </Flex>
                                )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}